/**
 * /unsubscribe?t=xxx — صفحة إلغاء اشتراك واتساب.
 * تستدعي server function تُسجّل الرقم في whatsapp_unsubscribes.
 */
import * as React from "react";
import { createFileRoute, Link, useSearch } from "@tanstack/react-router";
import { optStr } from "@/lib/search-validators";
import { useMutation } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { Loader2, CheckCircle2, XCircle } from "lucide-react";
import { useLocale } from "@/contexts/LocaleContext";
import { SiteShell } from "@/components/SiteShell";
import { Button } from "@/components/ui/button";
import { confirmUnsubscribe } from "@/utils/unsubscribe.functions";

const searchSchema = (search: Record<string, unknown>): { t?: string } => ({
  t: optStr(search.t),
});

export const Route = createFileRoute("/unsubscribe")({
  validateSearch: searchSchema,
  component: UnsubscribePage,
  head: () => ({
    meta: [
      { title: "Unsubscribe · Lecmo" },
      { name: "robots", content: "noindex" },
    ],
  }),
});

function UnsubscribePage() {
  const { t } = useSearch({ from: "/unsubscribe" });
  const { dir } = useLocale();
  const isAr = dir === "rtl";
  const confirmFn = useServerFn(confirmUnsubscribe);

  const mutation = useMutation({
    mutationFn: () => confirmFn({ data: { token: t ?? "" } }),
  });

  const tokenMissing = !t;
  const done = mutation.isSuccess && mutation.data?.success;
  const failed = mutation.isError || (mutation.isSuccess && !mutation.data?.success);

  return (
    <SiteShell>
      <div className="container max-w-md py-16">
        <div className="rounded-none border border-border bg-card p-8 text-center">
          {tokenMissing ? (
            <>
              <XCircle className="mx-auto mb-3 h-10 w-10 text-muted-foreground" />
              <h1 className="text-lg font-semibold">
                {isAr ? "رابط غير صالح" : "Invalid link"}
              </h1>
              <p className="mt-2 text-sm text-muted-foreground">
                {isAr
                  ? "هذا الرابط لا يحتوي على رمز إلغاء صالح."
                  : "This link is missing a valid token."}
              </p>
            </>
          ) : done ? (
            <>
              <CheckCircle2 className="mx-auto mb-3 h-10 w-10 text-emerald-500" />
              <h1 className="text-lg font-semibold">
                {isAr ? "تمّ إلغاء الاشتراك" : "Unsubscribed"}
              </h1>
              <p className="mt-2 text-sm text-muted-foreground">
                {isAr
                  ? "لن نُرسل لك رسائل واتساب تسويقيّة بعد الآن. تأكيدات الطلب وإشعارات الشحن ستظلّ تُرسَل لأنّها رسائل خدمة."
                  : "You won't receive marketing WhatsApp messages anymore. Order and shipping confirmations will still be sent as service messages."}
              </p>
            </>
          ) : failed ? (
            <>
              <XCircle className="mx-auto mb-3 h-10 w-10 text-destructive" />
              <h1 className="text-lg font-semibold">
                {isAr ? "تعذّر إلغاء الاشتراك" : "Unsubscribe failed"}
              </h1>
              <p className="mt-2 text-sm text-muted-foreground">
                {mutation.data?.error ??
                  (isAr ? "الرابط منتهٍ أو غير صحيح." : "The link is expired or invalid.")}
              </p>
            </>
          ) : (
            <>
              <h1 className="text-lg font-semibold">
                {isAr ? "إيقاف رسائل واتساب" : "Stop WhatsApp messages"}
              </h1>
              <p className="mt-3 text-sm text-muted-foreground">
                {isAr
                  ? "اضغط للتأكيد إذا كنت لا تريد استقبال رسائل واتساب التسويقيّة من Lecmo."
                  : "Confirm to stop receiving marketing WhatsApp messages from Lecmo."}
              </p>
              <Button
                className="mt-5 w-full"
                disabled={mutation.isPending}
                onClick={() => mutation.mutate()}
              >
                {mutation.isPending ? (
                  <>
                    <Loader2 className="me-2 h-4 w-4 animate-spin" />
                    {isAr ? "جاري الإلغاء..." : "Unsubscribing..."}
                  </>
                ) : isAr ? (
                  "تأكيد إلغاء الاشتراك"
                ) : (
                  "Confirm unsubscribe"
                )}
              </Button>
            </>
          )}

          <Link
            to="/"
            className="mt-6 inline-block text-sm text-muted-foreground underline"
          >
            {isAr ? "العودة إلى Lecmo" : "Back to Lecmo"}
          </Link>
        </div>
      </div>
    </SiteShell>
  );
}
